import React, {useState} from 'react';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Paper,
  Typography,
  Grid,
  CssBaseline,
  LinearProgress,
} from '@mui/material';
import {Visibility, VisibilityOff} from '@mui/icons-material';
import {useNavigate, useSearchParams} from 'react-router-dom';
import newLogo from 'src/assets/logo_no_background.png';
import {
  authServiceResetPassword,
  orgUserServiceSendInviteCallback,
} from 'src/apis/autoGenerate/services';
import message from 'src/components/Message';
import LoadingButton from '@mui/lab/LoadingButton';

interface ShowPasswordState {
  password: boolean;
  confirmPassword: boolean;
}

interface PasswordStrength {
  score: number; // 0-100
  messages: string[];
}

export default function SetupPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState<ShowPasswordState>({
    password: false,
    confirmPassword: false,
  });
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
    score: 0,
    messages: [],
  });

  // Determine if this is a password reset or new user setup
  const action = searchParams.get('action');
  const isPasswordReset = action === 'reset';

  const handleClickShowPassword = (key: keyof ShowPasswordState) =>
    setShowPassword({...showPassword, [key]: !showPassword[key]});

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const checkPasswordStrength = (password: string): PasswordStrength => {
    const strength: PasswordStrength = {
      score: 0,
      messages: [],
    };

    // 密码长度检查
    if (password.length < 8) {
      strength.messages.push('Password must be at least 8 characters');
    } else {
      strength.score += 25;
    }

    // 包含数字检查
    if (!/\d/.test(password)) {
      strength.messages.push('Include at least one number');
    } else {
      strength.score += 25;
    }

    // 包含小写字母检查
    if (!/[a-z]/.test(password)) {
      strength.messages.push('Include at least one lowercase letter');
    } else {
      strength.score += 25;
    }

    // 包含大写字母检查
    if (!/[A-Z]/.test(password)) {
      strength.messages.push('Include at least one uppercase letter');
    } else {
      strength.score += 25;
    }

    return strength;
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const password = event.target.value;
    setPasswordStrength(checkPasswordStrength(password));
  };

  const getStrengthColor = (score: number) => {
    if (score <= 25) return '#f44336'; // 红色
    if (score <= 50) return '#ff9800'; // 橙色
    if (score <= 75) return '#ffc107'; // 黄色
    return '#4caf50'; // 绿色
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const password = data.get('password') as string;
    const confirmPassword = data.get('confirmPassword') as string;

    // 添加密码强度验证
    if (passwordStrength.score < 100) {
      message.error('Please ensure your password meets all requirements');
      return;
    }

    if (password !== confirmPassword) {
      message.error('Passwords do not match');
      return;
    }

    const email = searchParams.get('email');
    const verifyCode = searchParams.get('verifyCode') || searchParams.get('token');
    const id = searchParams.get('id');

    if (!email || !verifyCode) {
      message.error('Invalid setup link');
      return;
    }

    // For org user invite callback (new user setup), we also need the id parameter
    if (!isPasswordReset && !id) {
      message.error('Invalid setup link - missing user ID');
      return;
    }

    setLoading(true);
    try {
      if (isPasswordReset) {
        // This is a password reset scenario
        await authServiceResetPassword({
          email,
          verifyCode,
          password,
        });
        message.success('Password reset successful');
      } else {
        // This is a new user setup scenario
        await orgUserServiceSendInviteCallback(id!, {
          email,
          verifyCode,
          password,
        });
        message.success('Password setup successful');
      }
      navigate('/login');
    } catch (error: any) {
      const errorMessage = isPasswordReset
        ? 'Failed to reset password'
        : 'Failed to setup password';
      message.error(error.message || errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container component="main" sx={{height: '100vh'}}>
      <CssBaseline />
      <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
        <Box
          sx={{
            my: 8,
            mx: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box sx={{mb: 4}}>
            <img src={newLogo} alt="Logo" style={{height: '128px'}} />
          </Box>
          <Typography component="h1" variant="h5" sx={{mb: 2}}>
            {isPasswordReset ? 'Reset Your Password' : 'Setup Your Password'}
          </Typography>
          <Typography color="text.secondary" sx={{mb: 4}}>
            {isPasswordReset
              ? 'Please enter your new password'
              : 'Please create a password for your account'}
          </Typography>
          <Box component="form" onSubmit={handleSubmit} sx={{mt: 1, width: '100%', maxWidth: 400}}>
            <FormControl fullWidth variant="outlined" sx={{mb: 1}}>
              <InputLabel htmlFor="password">Create Password *</InputLabel>
              <OutlinedInput
                id="password"
                name="password"
                type={showPassword.password ? 'text' : 'password'}
                required
                onChange={handlePasswordChange}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => handleClickShowPassword('password')}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword.password ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label="Create Password *"
              />
            </FormControl>

            {/* 密码强度指示器 */}
            <Box sx={{mb: 2}}>
              <LinearProgress
                variant="determinate"
                value={passwordStrength.score}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: getStrengthColor(passwordStrength.score),
                  },
                }}
              />

              {/* 密码要求提示 */}
              <Box sx={{mt: 1}}>
                {passwordStrength.messages.map((message, index) => (
                  <Typography
                    key={index}
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mt: 0.5,
                    }}
                  >
                    • {message}
                  </Typography>
                ))}
                {passwordStrength.score === 100 && (
                  <Typography variant="caption" color="success.main" display="block" sx={{mt: 0.5}}>
                    Password strength: Strong
                  </Typography>
                )}
              </Box>
            </Box>

            <FormControl fullWidth variant="outlined" sx={{mb: 3}}>
              <InputLabel htmlFor="confirmPassword">Confirm Password *</InputLabel>
              <OutlinedInput
                id="confirmPassword"
                name="confirmPassword"
                type={showPassword.confirmPassword ? 'text' : 'password'}
                required
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => handleClickShowPassword('confirmPassword')}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword.confirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label="Confirm Password *"
              />
            </FormControl>

            {/* Information box with permanent visibility */}
            <Box
              sx={{
                mt: 2,
                mb: 2,
                p: 2,
                bgcolor: 'info.lighter',
                borderRadius: 1,
                border: 1,
                borderColor: 'info.light',
              }}
            >
              <Typography
                variant="body2"
                color="info.main"
                align="left"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                ℹ️ Resident users: After setting up your password, please use the resident mobile
                app to log in.
              </Typography>
            </Box>

            <LoadingButton
              type="submit"
              fullWidth
              loading={loading}
              variant="contained"
              sx={{mt: 1, mb: 2}}
            >
              {isPasswordReset ? 'Reset Password' : 'Set Password'}
            </LoadingButton>
            <Button
              fullWidth
              variant="text"
              onClick={() => navigate('/login')}
              sx={{textTransform: 'none'}}
            >
              Back to Login
            </Button>
          </Box>
        </Box>
      </Grid>

      {/* Right section - Welcome Message */}
      <Grid
        item
        xs={false}
        sm={4}
        md={7}
        sx={{
          position: 'relative',
          bgcolor: '#f5f8fa',
          display: {xs: 'none', sm: 'block'}, // Hide on mobile
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: '#e8f0fe',
            backgroundImage: 'url(/abstract-pattern.png)',
            backgroundSize: 'cover',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            p: 4,
          }}
        >
          <Typography variant="h3" fontWeight="bold" color="#1A4065" mb={3}>
            Welcome to entrio
          </Typography>
          <Typography variant="h6" color="#555" textAlign="center" maxWidth={600}>
            Smart property management solutions for modern communities
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
}
